/**
 * globalPastoralCoherence — Checks that all active layers form a single coherent pastoral voice.
 *
 * Detects tonal contradictions, depth mismatches, pace conflicts, and over-discernment.
 * Goal: Kevia sounds like ONE presence, not a committee of diagnostic voices.
 *
 * Returns { coherenceState, contradictions, instruction, overDiscerned }
 */

const CONTRADICTIONS = [
  {
    name:        "tone_vs_grief",
    test:        ({ presence, emotion }) =>
      (presence === "celebrativa" || presence === "esperanzadora") &&
      (emotion?.dominant === "duelo" || emotion?.dominant === "dolor_profundo" || emotion?.dominant === "pérdida"),
    instruction: "Suaviza el tono. El dolor pide presencia, no celebración.",
  },
  {
    name:        "analysis_vs_grief",
    test:        ({ depth, emotion }) =>
      (depth === "deep" || depth === "reflective") &&
      (emotion?.dominant === "duelo" || emotion?.dominant === "quebranto"),
    instruction: "Reduce el análisis. El duelo pide compañía, no profundidad técnica.",
  },
  {
    name:        "action_vs_pause",
    test:        ({ convMode, pace }) =>
      (convMode === "analytical" || convMode === "planning") && pace === "pause_and_hold",
    instruction: "No es momento de planificar. Sostener primero.",
  },
  {
    name:        "encouragement_vs_exhaustion",
    test:        ({ presence, exhaustionState }) =>
      presence === "esperanzadora" &&
      (exhaustionState === "saturación_crítica" || exhaustionState === "desgaste_profundo"),
    instruction: "El cansancio pide descanso, no ánimo.",
  },
  {
    name:        "depth_vs_capacity",
    test:        ({ depth, capacity }) =>
      (depth === "deep" || depth === "reflective") &&
      (capacity === "overwhelmed" || capacity === "depleted"),
    instruction: "Reduce la profundidad. La capacidad está limitada.",
  },
];

export function assessGlobalCoherence({ presence, emotion, depth, convMode, pace, exhaustionState, capacity, activeBlockCount }) {
  const contradictions = [];

  for (const rule of CONTRADICTIONS) {
    if (rule.test({ presence, emotion, depth, convMode, pace, exhaustionState, capacity })) {
      contradictions.push({ name: rule.name, instruction: rule.instruction });
    }
  }

  // Over-discerned: many layers active but low emotional intensity
  const overDiscerned = (activeBlockCount ?? 0) >= 5 && (emotion?.intensity ?? 0) < 2;

  let coherenceState;
  let instruction = null;

  if (contradictions.length >= 2) {
    coherenceState = "contradictory";
    instruction    = contradictions[0].instruction;
  } else if (contradictions.length === 1 || overDiscerned) {
    coherenceState = "misaligned";
    instruction    = contradictions[0]?.instruction ?? "Simplifica la respuesta.";
  } else {
    coherenceState = "stable";
  }

  return { coherenceState, contradictions, instruction, overDiscerned };
}
